import { AlertCircle } from 'lucide-react';
import React from 'react';
import { Spinner } from '@/components/ui';

export interface Column<T> {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField?: string;
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  className?: string;
}

/**
 * Generic reusable data table component with support for custom column rendering,
 * alignment control, loading and error states, and optional row click handlers.
 * Follows the standard table styling pattern (divide-y, hover:bg-gray-50, etc.)
 */
function DataTableInner<T extends Record<string, any>>({
  columns,
  data,
  keyField = 'id',
  isLoading = false,
  isError = false,
  emptyMessage = 'No data available',
  onRowClick,
  className = '',
}: DataTableProps<T>) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" className="w-5 h-5 text-gray-400 animate-spin mr-2" />
        <span className="text-sm text-gray-400">Loading table data…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12 text-red-600">
        <AlertCircle className="w-5 h-5 mr-2" />
        <span className="text-sm">Error loading table data</span>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400">
        <span className="text-sm">{emptyMessage}</span>
      </div>
    );
  }

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            {columns.map(column => {
              const alignClass = {
                left: 'text-left',
                center: 'text-center',
                right: 'text-right',
              }[column.align || 'left'];

              return (
                <th
                  key={column.key}
                  className={`px-4 py-3 font-medium text-gray-700 ${alignClass} ${column.className || ''}`}
                >
                  {column.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.map((row, rowIdx) => {
            const key = row[keyField] ?? rowIdx;

            return (
              <tr
                key={key}
                onClick={() => onRowClick?.(row)}
                className={`${onRowClick ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''}`}
              >
                {columns.map(column => {
                  const alignClass = {
                    left: 'text-left',
                    center: 'text-center',
                    right: 'text-right',
                  }[column.align || 'left'];

                  const content = column.render ? column.render(row) : row[column.key];

                  return (
                    <td
                      key={column.key}
                      className={`px-4 py-3 text-gray-900 ${alignClass} ${column.className || ''}`}
                    >
                      {content}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Memoize while preserving generic type parameter
export const DataTable = React.memo(DataTableInner) as typeof DataTableInner;
